import React, { KeyboardEvent, ChangeEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty, remove } from 'lodash-es';
import { BasicInput, Tag, palette, Typography, LocalOfferRoundedIcon } from '@dev/ee-components';
import styled from 'styled-components';
import { EditTagsModalBodyProps } from './EditTagsModalBodyInterface';

const ContentWrapper = styled.div`
    color: ${palette.gray[6]};
`;
const TagWrapper = styled.div`
    padding-top: 16px;
    > div {
        margin-right: 8px;
        margin-bottom: 8px;
    }
`;
const InfoWrapper = styled.div`
    margin-bottom: 24px;
`;

const InputIcon = styled.div`
    left: 14px;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    svg {
        color: inherit;
    }
`;

export const EditTagsModalBody: React.FC<EditTagsModalBodyProps> = ({
    templateObject,
    onTagAdd,
    onTagRemove,
    isMobile,
}: EditTagsModalBodyProps): JSX.Element => {
    const tagInput = useRef<HTMLInputElement>();
    const { t } = useTranslation();
    const [isValid, setIsValid] = useState(true);
    const [tags, setTags] = useState(templateObject?.Tags || []);

    const addTag = (): void => {
        if (!isEmpty(tagInput.current.value) && isValid) {
            setTags([...tags, tagInput.current.value]);
            onTagAdd(tagInput.current.value);
            tagInput.current.value = '';
        }
    };

    const onEnter = (ev: KeyboardEvent<HTMLInputElement>): void => {
        if (ev.key === 'Enter') {
            addTag();
        }
    };

    const onChange = (ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (!tags || !tags.length) {
            return;
        }

        setIsValid(!tags?.includes(ev.currentTarget.value));
    };

    return (
        <ContentWrapper>
            <InfoWrapper>
                <Typography variant={'body1'} bodyVariant={'onPaper'}>
                    {t('addtagbypressingenter')}
                </Typography>
            </InfoWrapper>
            <BasicInput
                label={t('templatetags')}
                startIcon={
                    <InputIcon>
                        <LocalOfferRoundedIcon />
                    </InputIcon>
                }
                fullWidth
                style={{ minWidth: isMobile ? '100%' : '350px' }}
                onKeyUp={onEnter}
                autoFocus
                inputRef={tagInput}
                onChange={onChange}
                error={!isValid}
                inputProps={{
                    maxLength: 50,
                }}
                errorMessage={!isValid && t('tagbelongstotemplate')}
                actionButton={{
                    onClick: addTag,
                    disabled: !isValid,
                }}
            />
            <TagWrapper>
                {!isEmpty(tags) &&
                    tags?.map(
                        (elem, index): JSX.Element => (
                            <Tag
                                key={`tag-${index}`}
                                id={`tag-${index}`}
                                label={elem}
                                onDelete={(): void => {
                                    onTagRemove(elem);
                                    const newTags = remove(tags, (n) => n !== elem);
                                    setTags(newTags);
                                }}
                            />
                        )
                    )}
            </TagWrapper>
        </ContentWrapper>
    );
};
